import { fetchWithTimeoutAndRetry } from '@/api/client';
import { env } from '@/lib/env';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Hand-rolled identity/auth API client.
 *
 * We bypass `openapi-fetch` here because the generated schema (re-built by
 * the codegen pipeline) lags the backend during M3–M4 development. Once the
 * regenerated schema lands, callers can switch to `apiClient.POST(...)` with
 * no UI changes.
 */

export type TokensPayload = {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  access_expires_at: string;
  refresh_expires_at: string;
};

export type MembershipSummary = {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role: string;
  last_accessed_at: string | null;
};

export type LoginResponse =
  | { next: 'dashboard'; tokens: TokensPayload }
  | {
      next: 'select_tenant';
      pre_auth_token: string;
      memberships: MembershipSummary[];
    };

export type SignupResponse = {
  user_id: string;
  tenant_id: string;
  tenant_slug: string;
  status: string;
  verification_token_debug?: string | null;
};

export type ProblemDetail = {
  type?: string;
  title?: string;
  status?: number;
  code?: string;
  detail?: string;
  request_id?: string;
};

export class ApiError extends Error {
  status: number;
  problem: ProblemDetail;
  constructor(status: number, problem: ProblemDetail) {
    super(problem.detail || problem.title || `HTTP ${status}`);
    this.status = status;
    this.problem = problem;
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  bearer?: string | null;
  headers?: Record<string, string>;
};

let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  inFlightRefresh = (async () => {
    try {
      const res = await fetchWithTimeoutAndRetry(`${env.VITE_API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        // 401 means the refresh token itself was rejected — bounce to login.
        // 5xx is a server hiccup; let the caller surface the error and let
        // the user retry without losing their session.
        if (res.status === 401) {
          useAuthStore.getState().clearSession();
        }
        return null;
      }
      const tokens = (await res.json()) as TokensPayload;
      useAuthStore.setState({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
      });
      return tokens.access_token;
    } catch {
      // Network failure — don't wipe the session, let the caller surface the
      // error so the user can retry. We only clear on auth-failure responses
      // (handled above) where the refresh token is actually rejected.
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, bearer, headers = {} } = opts;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const buildHeaders = (token: string | null | undefined): Record<string, string> => {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    };
    if (token && !('Authorization' in h)) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  };
  const serialized = body === undefined ? undefined : JSON.stringify(body);

  const send = async (token: string | null | undefined) =>
    fetchWithTimeoutAndRetry(url, { method, headers: buildHeaders(token), body: serialized });

  let token = bearer ?? useAuthStore.getState().accessToken;
  let res = await send(token);

  // Auto-refresh once on expired access token (only when we weren't given an
  // explicit `bearer` — those callers manage their own token lifecycle).
  if (res.status === 401 && bearer === undefined) {
    let problemCode: string | undefined;
    try {
      problemCode = (await res.clone().json())?.error?.code;
    } catch {
      /* response wasn't JSON — fall through */
    }
    if (problemCode === 'token_expired') {
      const fresh = await refreshAccessToken();
      if (fresh) {
        token = fresh;
        res = await send(token);
      }
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const problem: ProblemDetail = data?.error ?? { detail: text };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

// ── Endpoint wrappers ──────────────────────────────────────────────────────
export const authApi = {
  signup: (payload: {
    email: string;
    password: string;
    full_name: string;
    org_name: string;
    country?: string;
    timezone?: string;
    industry?: string;
    accept_terms: boolean;
  }) => apiFetch<SignupResponse>('/auth/signup', { method: 'POST', body: payload }),

  verifyEmailByCode: (email: string, code: string) =>
    apiFetch<TokensPayload>('/auth/verify-email', {
      method: 'POST',
      body: { email, code },
    }),

  verifyEmailByLink: (token: string) => apiFetch<TokensPayload>(`/auth/verify-email/${token}`),

  resendVerification: (email: string) =>
    apiFetch<void>('/auth/resend-verification', {
      method: 'POST',
      body: { email },
    }),

  login: (email: string, password: string, remember_me = false) =>
    apiFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password, remember_me },
    }),

  selectTenant: (preAuthToken: string, tenant_id: string) =>
    apiFetch<TokensPayload>('/auth/select-tenant', {
      method: 'POST',
      body: { tenant_id },
      bearer: preAuthToken,
    }),

  refresh: (refresh_token: string) =>
    apiFetch<TokensPayload>('/auth/refresh', {
      method: 'POST',
      body: { refresh_token },
    }),

  logout: (refresh_token: string) =>
    apiFetch<void>('/auth/logout', { method: 'POST', body: { refresh_token } }),

  logoutAll: () => apiFetch<void>('/auth/logout-all', { method: 'POST' }),

  listSessions: () =>
    apiFetch<
      Array<{
        id: string;
        family_id: string;
        device_label: string | null;
        user_agent: string | null;
        ip_address: string | null;
        issued_at: string;
        last_used_at: string | null;
        expires_at: string;
      }>
    >('/auth/sessions'),

  revokeSession: (sessionId: string) =>
    apiFetch<void>(`/auth/sessions/${sessionId}`, { method: 'DELETE' }),

  requestPasswordReset: (email: string) =>
    apiFetch<void>('/auth/password-reset', { method: 'POST', body: { email } }),

  confirmPasswordReset: (token: string, new_password: string) =>
    apiFetch<void>('/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, new_password },
    }),

  acceptInvite: (token: string, full_name?: string, password?: string) =>
    apiFetch<TokensPayload>('/auth/accept-invite', {
      method: 'POST',
      body: { token, full_name, password },
    }),

  me: () =>
    apiFetch<{
      user_id: string;
      email: string;
      full_name: string;
      tenant_id: string;
      tenant_name: string;
      role: string;
      permissions: string[];
    }>('/me'),

  myTenants: () => apiFetch<MembershipSummary[]>('/me/tenant-memberships'),
};

export const tenantApi = {
  current: () =>
    apiFetch<{
      id: string;
      name: string;
      slug: string;
      status: string;
      country: string | null;
      timezone: string | null;
      industry: string | null;
    }>('/tenant'),

  invites: () =>
    apiFetch<
      Array<{
        id: string;
        email: string;
        role: string;
        status: string;
        expires_at: string;
        invited_by_user_id: string;
      }>
    >('/tenant/invites'),

  createInvites: (payload: { emails: string[]; role: string; personal_message?: string }) =>
    apiFetch('/tenant/invites', { method: 'POST', body: payload }),

  revokeInvite: (id: string) => apiFetch<void>(`/tenant/invites/${id}`, { method: 'DELETE' }),

  roles: () =>
    apiFetch<
      Array<{
        id: string;
        slug: string;
        name: string;
        description: string | null;
        permissions: string[];
        is_system_default: boolean;
        cloned_from: string | null;
      }>
    >('/tenant/roles'),

  createRole: (payload: {
    slug: string;
    name: string;
    description?: string;
    permissions: string[];
  }) => apiFetch('/tenant/roles', { method: 'POST', body: payload }),

  cloneRole: (source_slug: string, new_slug: string, new_name: string) =>
    apiFetch('/tenant/roles/clone', {
      method: 'POST',
      body: { source_slug, new_slug, new_name },
    }),

  updateRole: (
    id: string,
    payload: { name?: string; description?: string; permissions?: string[] },
  ) => apiFetch(`/tenant/roles/${id}`, { method: 'PATCH', body: payload }),

  deleteRole: (id: string) => apiFetch<void>(`/tenant/roles/${id}`, { method: 'DELETE' }),

  members: () =>
    apiFetch<
      Array<{
        user_id: string;
        email: string;
        full_name: string;
        role: string;
        custom_role_id: string | null;
        is_active: boolean;
        joined_at: string;
        last_accessed_at: string | null;
      }>
    >('/tenant/members'),

  updateMemberRole: (user_id: string, role: string, custom_role_id?: string | null) =>
    apiFetch(`/tenant/members/${user_id}`, {
      method: 'PATCH',
      body: { role, custom_role_id: custom_role_id ?? null },
    }),

  deactivateMember: (user_id: string) =>
    apiFetch(`/tenant/members/${user_id}/deactivate`, { method: 'POST' }),

  reactivateMember: (user_id: string) =>
    apiFetch(`/tenant/members/${user_id}/reactivate`, { method: 'POST' }),

  cancelTenant: () => apiFetch<void>('/tenant/cancel', { method: 'POST' }),

  reactivateTenant: () => apiFetch('/tenant/reactivate', { method: 'POST' }),
};

export const permissionsCatalog: { code: string; label: string; group: string }[] = [
  // Tenant / org
  { code: 'tenant.read', label: 'Read tenant', group: 'Tenant' },
  { code: 'tenant.update', label: 'Update tenant', group: 'Tenant' },
  { code: 'tenant.cancel', label: 'Cancel tenant', group: 'Tenant' },
  { code: 'tenant.configure', label: 'Configure tenant', group: 'Tenant' },
  // Users + roles
  { code: 'user.read', label: 'Read users', group: 'Users & roles' },
  { code: 'user.invite', label: 'Invite users', group: 'Users & roles' },
  { code: 'user.update', label: 'Update users', group: 'Users & roles' },
  { code: 'user.deactivate', label: 'Deactivate users', group: 'Users & roles' },
  { code: 'role.read', label: 'Read roles', group: 'Users & roles' },
  { code: 'role.create', label: 'Create roles', group: 'Users & roles' },
  { code: 'role.update', label: 'Update roles', group: 'Users & roles' },
  { code: 'role.delete', label: 'Delete roles', group: 'Users & roles' },
  // Field users
  { code: 'field_user.read', label: 'Read field users', group: 'Field users' },
  { code: 'field_user.create', label: 'Create field users', group: 'Field users' },
  { code: 'field_user.update', label: 'Update field users', group: 'Field users' },
  { code: 'field_user.deactivate', label: 'Deactivate field users', group: 'Field users' },
  { code: 'field_user.delete', label: 'Delete field users', group: 'Field users' },
  { code: 'field_user.provision', label: 'Generate setup cards', group: 'Field users' },
  { code: 'field_user.reset_pin', label: 'Reset PINs', group: 'Field users' },
  // Assets
  { code: 'asset.read', label: 'Read assets', group: 'Assets' },
  { code: 'asset.create', label: 'Create assets', group: 'Assets' },
  { code: 'asset.update', label: 'Update assets', group: 'Assets' },
  { code: 'asset.delete', label: 'Delete assets', group: 'Assets' },
  // Engagements
  { code: 'engagement.read', label: 'Read engagements', group: 'Engagements' },
  { code: 'engagement.create', label: 'Create engagements', group: 'Engagements' },
  { code: 'engagement.update', label: 'Update engagements', group: 'Engagements' },
  // Documents
  { code: 'document.read', label: 'Read documents', group: 'Documents' },
  { code: 'document.upload', label: 'Upload documents', group: 'Documents' },
  { code: 'document.create', label: 'Create document types', group: 'Documents' },
  { code: 'document.delete', label: 'Delete documents', group: 'Documents' },
  // Operations
  { code: 'work_unit.read', label: 'Read work units', group: 'Operations' },
  { code: 'duty.read', label: 'Read duties', group: 'Operations' },
  { code: 'fuel.read', label: 'Read fuel', group: 'Operations' },
  { code: 'fuel.create', label: 'Log fuel', group: 'Operations' },
  { code: 'maintenance.read', label: 'Read maintenance', group: 'Operations' },
  { code: 'maintenance.create', label: 'Create maintenance', group: 'Operations' },
  { code: 'maintenance.update', label: 'Update maintenance', group: 'Operations' },
  { code: 'maintenance.triage', label: 'Triage maintenance', group: 'Operations' },
  // Attendance
  { code: 'attendance.read', label: 'Read attendance', group: 'Attendance' },
  { code: 'attendance.update', label: 'Update attendance', group: 'Attendance' },
  { code: 'leave.read', label: 'Read leave', group: 'Attendance' },
  { code: 'leave.create', label: 'Request leave', group: 'Attendance' },
  { code: 'leave.approve', label: 'Approve leave', group: 'Attendance' },
  // Financial
  { code: 'advance.read', label: 'Read advances', group: 'Financial' },
  { code: 'advance.create', label: 'Request advances', group: 'Financial' },
  { code: 'advance.approve', label: 'Approve advances', group: 'Financial' },
  { code: 'fine.read', label: 'Read fines', group: 'Financial' },
  { code: 'fine.create', label: 'Issue fines', group: 'Financial' },
  { code: 'payroll.read', label: 'Read payroll', group: 'Financial' },
  { code: 'payroll.run', label: 'Run payroll', group: 'Financial' },
  { code: 'salary_structure.read', label: 'Read salary structures', group: 'Financial' },
  { code: 'salary_structure.update', label: 'Update salary structures', group: 'Financial' },
  // Reports
  { code: 'report.read', label: 'Read reports', group: 'Reports' },
  { code: 'report.export', label: 'Export reports', group: 'Reports' },
  // Communication
  { code: 'conversation.read', label: 'Read conversations', group: 'Communication' },
  { code: 'conversation.send', label: 'Send messages', group: 'Communication' },
  { code: 'broadcast.send', label: 'Send broadcasts', group: 'Communication' },
  // Audit
  { code: 'audit.read', label: 'Read audit log', group: 'Audit' },
];

export const fieldUserApi = {
  list: () =>
    apiFetch<
      Array<{
        id: string;
        employee_id: string;
        full_name: string;
        phone: string | null;
        photo_path: string | null;
        status: string;
        hired_at: string | null;
        last_login_at: string | null;
      }>
    >('/field-users'),

  create: (payload: {
    employee_id: string;
    full_name: string;
    phone?: string;
    photo_path?: string;
    hired_at?: string;
  }) => apiFetch('/field-users', { method: 'POST', body: payload }),

  get: (id: string) => apiFetch(`/field-users/${id}`),

  regenerateCard: (id: string) =>
    apiFetch<{
      field_user_id: string;
      code: string;
      qr_payload: string;
      expires_at: string;
      max_uses: number;
    }>(`/field-users/${id}/regenerate-setup-card`, { method: 'POST' }),

  /**
   * Read-only lookup behind the shareable `/card/:employeeId` link. Returns
   * the live code without minting a new one — `code` is null when the user
   * has no card issued yet.
   */
  cardByEmployeeId: (employeeId: string) =>
    apiFetch<{
      field_user_id: string;
      employee_id: string;
      full_name: string;
      status: string;
      code: string | null;
      qr_payload: string | null;
      expires_at: string | null;
      max_uses: number | null;
    }>(`/field-users/card/${encodeURIComponent(employeeId)}`),

  resetPin: (id: string) =>
    apiFetch<{
      field_user_id: string;
      code: string;
      qr_payload: string;
      expires_at: string;
      max_uses: number;
    }>(`/field-users/${id}/reset-pin`, { method: 'POST' }),

  deactivate: (id: string) => apiFetch(`/field-users/${id}/deactivate`, { method: 'POST' }),

  reactivate: (id: string) => apiFetch(`/field-users/${id}/reactivate`, { method: 'POST' }),

  remove: (id: string) => apiFetch(`/field-users/${id}`, { method: 'DELETE' }),
};

export const onboardingApi = {
  templates: () =>
    apiFetch<
      Array<{
        id: string;
        slug: string;
        name: string;
        summary: string;
        icon: string | null;
        terminology: Record<string, string>;
        asset_types: Array<{ slug: string; name: string }>;
        document_types: Array<{ slug: string; name: string }>;
      }>
    >('/onboarding/templates'),

  state: () =>
    apiFetch<{
      tenant_id: string;
      current_step: number;
      total_steps: number;
      completed_steps: Record<string, boolean>;
      chosen_template: string | null;
      is_complete: boolean;
    }>('/onboarding/state'),

  /**
   * One-shot setup. Applies the industry template, writes the terminology,
   * materializes the asset-type + document-type catalogs and activates the
   * tenant — there are no steps to submit or skip.
   */
  setup: (template_slug: string, terminology: Record<string, string>) =>
    apiFetch('/onboarding/setup', {
      method: 'POST',
      body: { template_slug, terminology },
    }),
};
